import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client';
import {
  useAcceptAllSuggestions,
  useAcceptSuggestion,
  useAllocatePayment,
  useCan,
  useCompanies,
  useImportBankCsv,
  useMatchPayment,
  usePaymentCandidates,
  usePaymentSuggestions,
  usePayments,
  useSkipSuggestions,
} from '../api/hooks';
import type {
  BankImportResult,
  MatchReason,
  PaymentListItem,
  PaymentMethod,
  PaymentSuggestionGroup,
  SuggestionDoc,
} from '../api/types';
import { ExportButtons } from '../components/ExportButtons';
import { I } from '../components/Icons';
import { LoadMore } from '../components/LoadMore';
import { PayRow, PayTable } from '../components/PayRow';
import { Btn, Empty, Field, FileBtn, Row, Surface, Tag } from '../components/ui';
import { useT } from '../i18n';
import { METHODS } from '../lib/doc';
import { czk, czk0, dt, MONTHS_IN, money, plural, todayIso } from '../lib/format';
import { useCompanyFilter } from '../state/company';
import { useTopbar } from '../state/topbar';
import { useUi } from '../state/ui';

const BANK_LABEL = {
  fio: 'Fio',
  cs: 'Česká spořitelna',
  kb: 'Komerční banka',
  gpc: 'Výpis GPC',
  camt: 'Výpis XML (CAMT)',
} as const;

export function importResultText(r: BankImportResult): string {
  const base = `${BANK_LABEL[r.bank]}: ${r.imported} ${plural(r.imported, ['nová platba', 'nové platby', 'nových plateb'])}, ${r.matched} ${plural(r.matched, ['spárovaná', 'spárované', 'spárovaných'])}, ${r.duplicates} ${plural(r.duplicates, ['duplicitní', 'duplicitní', 'duplicitních'])}, ${r.outgoingMatched ? `${r.outgoingMatched} ${plural(r.outgoingMatched, ['úhrada přijaté faktury', 'úhrady přijatých faktur', 'úhrad přijatých faktur'])}, ` : ''}${r.skippedOutgoing} ${plural(r.skippedOutgoing, ['odchozí bez faktury přeskočena', 'odchozí bez faktury přeskočeny', 'odchozích bez faktury přeskočeno'])}`;
  return r.suggestions
    ? `${base}, ${r.suggestions} ${plural(r.suggestions, ['návrh k potvrzení', 'návrhy k potvrzení', 'návrhů k potvrzení'])}`
    : base;
}

export const REASON_LABEL: Record<MatchReason, string> = {
  vs: 'sedí VS',
  amount: 'sedí částka',
  overpayment: 'přeplatek',
  payer_name: 'sedí plátce',
  payer_account: 'sedí účet plátce',
};

/** „sedí částka i plátce" – pravidlo 4 */
export function reasonText(reasons: MatchReason[]): string {
  const labels = reasons.map((r) => REASON_LABEL[r]).filter(Boolean);
  if (!labels.length) return '';
  const [first, ...rest] = labels;
  return rest.length
    ? `${first} i ${rest.map((l) => l.replace(/^sedí /, '')).join(' i ')}`
    : (first as string);
}

/** Souhrn importu ve třech skupinách (pravidlo 5). */
function ImportSummary({ result: r, onClose }: { result: BankImportResult; onClose: () => void }) {
  const g = r.groups;
  return (
    <Surface className="pad mt">
      <div data-testid="import-summary">
        <div className="l1">{importResultText(r)}</div>
        {g && (
          <div className="mt">
            <div className="k">Spárováno podle VS ({g.matched.length})</div>
            {g.matched.map((m) => (
              <div className="l2" key={m.payment.id}>
                {money(m.payment.amount, m.payment.currency ?? 'CZK')} → {m.document.number} ·{' '}
                {m.document.customerName}
              </div>
            ))}
            <div className="k mt">Návrhy k potvrzení ({g.suggested.length})</div>
            {g.suggested.map((sg) => (
              <div className="l2" key={sg.payment.id}>
                {money(sg.payment.amount, sg.payment.currency ?? 'CZK')} od{' '}
                {sg.payment.payerName || 'neznámého plátce'} →{' '}
                {sg.candidates
                  .map((c) => `${c.document.number} (${reasonText(c.reasons)})`)
                  .join(', ')}
              </div>
            ))}
            <div className="k mt">Bez shody ({g.unmatched.length})</div>
            {g.unmatched.map((u) => (
              <div className="l2" key={u.id}>
                {money(u.amount, u.currency ?? 'CZK')} od {u.payerName || 'neznámého plátce'}
                {u.variableSymbol ? `, VS ${u.variableSymbol}` : ''}
              </div>
            ))}
          </div>
        )}
        <Btn sm className="mt" onClick={onClose}>
          Zavřít souhrn
        </Btn>
      </div>
    </Surface>
  );
}

/** Návrhy k potvrzení – zůstávají, dokud je někdo nevyřídí (pravidlo 5, 6). */
function SuggestionsBlock({
  groups,
  companyId,
  canWrite,
}: {
  groups: PaymentSuggestionGroup[];
  companyId: string;
  canWrite: boolean;
}) {
  const accept = useAcceptSuggestion();
  const skip = useSkipSuggestions();
  const acceptAll = useAcceptAllSuggestions();
  const { toast } = useUi();
  const single = groups.filter((g) => g.candidates.length === 1).length;
  return (
    <section className="mt" data-testid="suggestions">
      <div className="listmeta">
        <span>
          <b>Návrhy k potvrzení</b> ({groups.length})
        </span>
        {canWrite && single > 0 && (
          <Btn
            sm
            variant="primary"
            loading={acceptAll.isPending}
            onClick={() =>
              acceptAll.mutate(companyId, {
                onSuccess: (r) =>
                  toast(
                    `Potvrzeno ${r.accepted}${r.needChoice ? `, ${r.needChoice} čeká na volbu dokladu` : ''}`,
                  ),
                onError: (e) => toast(e.message),
              })
            }
          >
            Potvrdit všechny návrhy
          </Btn>
        )}
      </div>
      <Surface>
        {groups.map((g) => (
          <div className="row" key={g.payment.id} data-testid="suggestion">
            <div className="grow">
              <div className="l1">
                {money(g.payment.amount, g.payment.currency ?? 'CZK')} od{' '}
                {g.payment.payerName || 'neznámého plátce'}, {dt(g.payment.date)}
                {g.payment.variableSymbol ? `, VS ${g.payment.variableSymbol}` : ''}
                {g.candidates.length > 1 && <Tag>vyber doklad</Tag>}
              </div>
              {g.candidates.map((c) => (
                <div className="l2 suggestion-candidate" key={c.suggestionId}>
                  <span>
                    {c.document.number} · {c.document.customerName} · zbývá{' '}
                    {czk(c.document.remaining)}
                  </span>
                  <Tag ok={!c.reasons.includes('overpayment')}>{reasonText(c.reasons)}</Tag>
                  {canWrite && (
                    <Btn
                      sm
                      loading={accept.isPending && accept.variables === c.suggestionId}
                      onClick={() =>
                        accept.mutate(c.suggestionId, {
                          onSuccess: ({ document }) =>
                            toast(
                              document.status === 'paid'
                                ? `${document.number} je zaplaceno`
                                : `Spárováno, u ${document.number} zbývá ${czk(document.remaining)}`,
                            ),
                          onError: (e) => toast(e.message),
                        })
                      }
                    >
                      Spárovat
                    </Btn>
                  )}
                </div>
              ))}
            </div>
            {canWrite && (
              <Btn
                sm
                onClick={() => skip.mutate(g.payment.id, { onError: (e) => toast(e.message) })}
              >
                Přeskočit
              </Btn>
            )}
          </div>
        ))}
      </Surface>
    </section>
  );
}

export function Payments() {
  const t = useT();
  useTopbar(t('nav.payments'), { right: 'companyChip' });
  const navigate = useNavigate();
  const { activeCompanyId } = useCompanyFilter();
  const { data: companies = [] } = useCompanies();
  const [tab, setTab] = useState<'all' | 'un'>('all');
  const sid = activeCompanyId ?? undefined;
  const m = todayIso().slice(0, 7);
  const allQ = usePayments({ companyId: sid });
  const unQ = usePayments({ companyId: sid, unmatched: true });
  const all = allQ.data;
  const unmatched = unQ.data;
  // statistika měsíce z vlastního dotazu, seznam je stránkovaný (fáze 15)
  const { data: monthPays = [] } = usePayments({ companyId: sid, month: m });
  const importCsv = useImportBankCsv();
  const access = useCan(activeCompanyId);
  const canWrite = !access.loaded || access.can('payments:write');
  const { openSheet, closeSheet, toast } = useUi();
  const [importMsg, setImportMsg] = useState<BankImportResult | null>(null);
  const { data: suggestionGroups = [] } = usePaymentSuggestions(sid);

  const monthIncoming = monthPays.filter((p) => Number(p.amount) > 0 && p.direction !== 'out');
  const byMethod: Partial<Record<PaymentMethod, number>> = {};
  for (const p of monthIncoming) byMethod[p.method] = (byMethod[p.method] ?? 0) + Number(p.amount);
  const monthSum = monthIncoming.reduce((a, p) => a + Number(p.amount), 0);
  const list = tab === 'un' ? unmatched : all;

  const importFile = (file: File | undefined) => {
    if (!file) return;
    const effective = sid ?? companies[0]?.id;
    if (!effective) {
      toast('Nejdřív založ firmu');
      return;
    }
    const form = new FormData();
    form.set('companyId', effective);
    form.set('file', file);
    setImportMsg(null);
    importCsv.mutate(form, {
      onSuccess: (r) => setImportMsg(r),
      onError: (err) => toast(err instanceof ApiError ? err.message : 'Import se nezdařil'),
    });
  };

  const openMatch = (p: PaymentListItem) =>
    openSheet(
      <MatchSheet
        payment={p}
        onDone={(msg) => {
          closeSheet();
          toast(msg);
        }}
      />,
    );
  const openSplit = (p: PaymentListItem) =>
    openSheet(
      <SplitSheet
        payment={p}
        onDone={(msg) => {
          closeSheet();
          toast(msg);
        }}
      />,
    );

  return (
    <>
      <Surface className="pad">
        <div className="pay-sum-head">
          <div>
            <div className="k">Přijato v {MONTHS_IN[Number(m.slice(5)) - 1]}</div>
            <div className="v ok">{czk0(monthSum)}</div>
          </div>
          <ExportButtons kind="payments" companyId={activeCompanyId} />
        </div>
        <div className="bymethod">
          {Object.keys(byMethod).length ? (
            (Object.entries(byMethod) as [PaymentMethod, number][]).map(([k, v]) => (
              <span key={k}>
                {I[k]}
                {METHODS[k]} <b>{czk0(v)}</b>
              </span>
            ))
          ) : (
            <span>Zatím nic</span>
          )}
        </div>
      </Surface>
      {suggestionGroups.length > 0 && (
        <SuggestionsBlock groups={suggestionGroups} companyId={sid ?? ''} canWrite={canWrite} />
      )}
      <div className="seg mt">
        <button type="button" className={tab === 'all' ? 'on' : ''} onClick={() => setTab('all')}>
          Všechny platby
        </button>
        <button type="button" className={tab === 'un' ? 'on' : ''} onClick={() => setTab('un')}>
          Nespárované{(unQ.total ?? unmatched.length) ? ` (${unQ.total ?? unmatched.length})` : ''}
        </button>
      </div>
      <PayTable className="mt">
        {list.length ? (
          list.map((p) => (
            <PayRow
              key={p.id}
              payment={p}
              onMatch={canWrite ? openMatch : undefined}
              onSplit={canWrite ? openSplit : undefined}
            />
          ))
        ) : (
          <Empty>
            {tab === 'un' ? 'Všechny platby jsou spárované s doklady.' : 'Zatím žádná platba.'}
          </Empty>
        )}
      </PayTable>
      <LoadMore
        hasMore={tab === 'un' ? unQ.hasNextPage : allQ.hasNextPage}
        loading={tab === 'un' ? unQ.isFetchingNextPage : allQ.isFetchingNextPage}
        onMore={tab === 'un' ? unQ.fetchNextPage : allQ.fetchNextPage}
      />
      {importMsg && <ImportSummary result={importMsg} onClose={() => setImportMsg(null)} />}
      <div className="actions">
        {canWrite && (
          <FileBtn
            label="Nahrát výpis z banky"
            accept=".csv,.gpc,.abo,.xml,.txt,text/csv,text/xml,application/xml"
            loading={importCsv.isPending}
            onFiles={(files) => importFile(files[0])}
          />
        )}
        <Btn onClick={() => navigate('/doklady')}>Otevřené doklady</Btn>
      </div>
      <p className="hint">
        Výpis z banky ve formátu GPC (ABO), XML CAMT.053 nebo CSV z Fio, České spořitelny či
        Komerční banky. Příchozí platby se spárují s vydanými fakturami, odchozí s přijatými (úhrady
        dodavatelům) – podle variabilního symbolu, zbytek navrhne podle částky.
      </p>
    </>
  );
}

/** Rozdělení platby na víc dokladů (kap. 19): kandidáti = otevřené doklady, zbytek = přeplatek. */
function SplitSheet({
  payment: p,
  onDone,
}: {
  payment: PaymentListItem;
  onDone: (message: string) => void;
}) {
  const { data: candidates = [] } = usePaymentCandidates(p.id);
  const allocate = useAllocatePayment(p.id);
  const { closeSheet, toast } = useUi();
  const cur = p.currency ?? 'CZK';
  const initial: Record<string, string> = {};
  for (const a of p.allocations ?? []) initial[a.documentId] = a.amount;
  const [amounts, setAmounts] = useState<Record<string, string>>(initial);
  const docs = [
    ...(p.allocations ?? []).map((a) => ({
      id: a.documentId,
      number: a.document?.number ?? '',
      customerName: a.document?.customerName ?? '',
      remaining: null as string | null,
    })),
    ...candidates
      .filter((c) => !(p.allocations ?? []).some((a) => a.documentId === c.id))
      .map((c) => ({
        id: c.id,
        number: c.number ?? '',
        customerName: c.customerName,
        remaining: c.remaining,
      })),
  ];
  const sum = Object.values(amounts).reduce((a, v) => a + (Number(v.replace(',', '.')) || 0), 0);
  const rest = Math.round((Math.abs(Number(p.amount)) - sum) * 100) / 100;

  const submit = () => {
    const list = Object.entries(amounts)
      .map(([documentId, v]) => ({ documentId, amount: v.replace(',', '.').trim() }))
      .filter((a) => Number(a.amount) > 0);
    if (!list.length) {
      toast('Zadej aspoň jednu částku');
      return;
    }
    allocate.mutate(list, {
      onSuccess: ({ payment }) =>
        onDone(
          Number(payment.unallocated) > 0
            ? `Rozděleno, přeplatek ${money(payment.unallocated ?? 0, cur)}`
            : 'Platba rozdělena',
        ),
      onError: (err) => toast(err.message),
    });
  };

  return (
    <>
      <h3>Rozdělit platbu</h3>
      <div className="muted mb">
        {money(p.amount, cur)} od {p.payerName || 'neznámého plátce'}, {dt(p.date)}
      </div>
      <Surface>
        {docs.map((d) => (
          <div className="row" key={d.id}>
            <div className="grow">
              <div className="l1">{d.number}</div>
              <div className="l2">
                <span>{d.customerName}</span>
                {d.remaining && <span>zbývá {money(d.remaining, cur)}</span>}
              </div>
            </div>
            <Field
              id={`alloc-${d.id}`}
              label={`Částka na ${d.number}`}
              inputMode="decimal"
              className="m0"
              value={amounts[d.id] ?? ''}
              onChange={(e) => setAmounts((o) => ({ ...o, [d.id]: e.target.value }))}
              style={{ width: 120 }}
            />
          </div>
        ))}
        {!docs.length && <Empty>Žádný otevřený doklad k rozdělení.</Empty>}
      </Surface>
      <p className={`hint ${rest < 0 ? 'bad' : ''}`}>
        {rest < 0
          ? `Součet převyšuje platbu o ${money(-rest, cur)}`
          : rest > 0
            ? `Nerozděleno ${money(rest, cur)} – zůstane jako přeplatek`
            : 'Celá platba je rozdělená'}
      </p>
      <Btn variant="primary" full loading={allocate.isPending} disabled={rest < 0} onClick={submit}>
        Uložit rozdělení
      </Btn>
      <Btn full onClick={closeSheet}>
        Zavřít
      </Btn>
    </>
  );
}

/** Sheet „Spárovat platbu" s návrhy podle VS a částky (matchPay z prototypu). */
function MatchSheet({
  payment: p,
  onDone,
}: {
  payment: PaymentListItem;
  onDone: (message: string) => void;
}) {
  const { data: candidates = [], isPending } = usePaymentCandidates(p.id);
  const match = useMatchPayment(p.id);
  const { closeSheet, toast } = useUi();
  return (
    <>
      <h3>Spárovat platbu</h3>
      <div className="muted mb">
        {money(p.amount, p.currency ?? 'CZK')} od {p.payerName || 'neznámého plátce'}, {dt(p.date)}
        {p.variableSymbol ? `, VS ${p.variableSymbol}` : ''}
      </div>
      {candidates.length ? (
        <Surface>
          {candidates.map((d) => (
            <Row
              key={d.id}
              onClick={() =>
                match.mutate(d.id, {
                  onSuccess: ({ document }) =>
                    onDone(
                      document.status === 'paid'
                        ? `${document.number} je zaplaceno`
                        : `Spárováno, u ${document.number} zbývá ${czk(document.remaining)}`,
                    ),
                  onError: (err) => toast(err.message),
                })
              }
            >
              <div className="grow">
                <div className="l1">
                  {d.number} {d.matchesVs && <Tag ok>odpovídá VS</Tag>}
                </div>
                <div className="l2">
                  <span>{d.customerName}</span>
                  <span>zbývá {czk(d.remaining)}</span>
                </div>
              </div>
            </Row>
          ))}
        </Surface>
      ) : (
        <Empty>
          {isPending
            ? 'Hledám doklady…'
            : 'Žádný otevřený doklad. Platbu můžeš nechat nespárovanou nebo k ní vystavit doklad.'}
        </Empty>
      )}
      <Btn full className="mt" onClick={closeSheet}>
        Zavřít
      </Btn>
    </>
  );
}
